// components/partial/DepositRefundModal.jsx
import React, { Component } from 'react';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';

class DepositRefundModal extends Component {
  state = {
    refundChoice: null, // 'full', 'partial', 'none'
    partialAmount: '',
    processing: false
  };

  handleChoiceSelect = (choice) => {
    this.setState({
      refundChoice: choice,
      partialAmount: choice === 'full' ? this.getDepositAmount() : ''
    });
  };

  handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow empty, or valid decimal numbers
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      this.setState({ partialAmount: value });
    }
  };

  getDepositAmount = () => {
    const { depositPayment } = this.props;
    return depositPayment ? parseFloat(depositPayment.payment_amount) : 0;
  };

  handleConfirm = () => {
    const { refundChoice, partialAmount } = this.state;
    const depositAmount = this.getDepositAmount();

    if (refundChoice === 'partial') {
      const amount = parseFloat(partialAmount);
      if (!amount || amount <= 0) {
        alert('Please enter a refund amount greater than $0');
        return;
      }
      if (amount > depositAmount) {
        alert(`Refund amount cannot exceed the deposit of $${depositAmount.toFixed(2)}`);
        return;
      }
    }

    this.setState({ processing: true });

    const refundAmount = refundChoice === 'full' ? depositAmount :
                         refundChoice === 'partial' ? parseFloat(partialAmount) : 0;

    this.props.onRefundDecision({
      refundType: refundChoice,
      refundAmount: refundAmount,
      skipRefund: refundChoice === 'none'
    });
  };

  handleCancel = () => {
    this.props.onHide();
    this.resetState();
  };

  resetState = () => {
    this.setState({
      refundChoice: null,
      partialAmount: '',
      processing: false
    });
  };

  render() {
    const { show, request, depositPayment } = this.props;
    const { refundChoice, partialAmount, processing } = this.state;

    if (!request || !depositPayment) return null;

    const depositAmount = this.getDepositAmount();
    const vehicleInfo = `${request.vehicleYear} ${request.vehicleMake} ${request.vehicleModel}`;

    return (
      <Modal show={show} onHide={this.handleCancel} centered size="lg" onExited={this.resetState}>
        <Modal.Header closeButton>
          <Modal.Title>Deposit Refund</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="job-info mb-3 p-3 bg-light rounded">
            <h6 className="mb-1">{vehicleInfo}</h6>
            <p className="text-muted mb-1">{request.serviceRequested}</p>
            <p className="mb-0"><strong>Request ID:</strong> {request.requestId}</p>
          </div>

          <div className="alert alert-warning mb-4">
            <strong>Deposit Paid:</strong> ${depositAmount.toFixed(2)}
            <br />
            <small className="text-muted">
              This request has a paid deposit. How would you like to handle the refund before cancelling?
            </small>
          </div>

          <div className="action-selection mb-3">
            <p className="font-weight-bold mb-3">Choose a refund option:</p>

            <div className="action-buttons d-flex flex-column gap-2 mb-3">
              <Button
                variant={refundChoice === 'full' ? 'success' : 'outline-success'}
                className="text-left py-3 px-3"
                onClick={() => this.handleChoiceSelect('full')}
              >
                <div>
                  <div className="font-weight-bold">Full Refund</div>
                  <small className="text-muted">
                    Refund the full ${depositAmount.toFixed(2)} to the customer
                  </small>
                </div>
              </Button>

              <Button
                variant={refundChoice === 'partial' ? 'warning' : 'outline-warning'}
                className="text-left py-3 px-3"
                onClick={() => this.handleChoiceSelect('partial')}
              >
                <div>
                  <div className="font-weight-bold">Partial Refund</div>
                  <small className="text-muted">
                    Refund a portion of the deposit
                  </small>
                </div>
              </Button>

              <Button
                variant={refundChoice === 'none' ? 'danger' : 'outline-danger'}
                className="text-left py-3 px-3"
                onClick={() => this.handleChoiceSelect('none')}
              >
                <div>
                  <div className="font-weight-bold">No Refund</div>
                  <small className="text-muted">
                    Keep the deposit (no refund issued)
                  </small>
                </div>
              </Button>
            </div>
          </div>

          {refundChoice === 'partial' && (
            <div className="partial-amount-section mb-3">
              <Form.Group>
                <Form.Label className="font-weight-bold">
                  Refund Amount <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <InputGroup.Prepend>
                    <InputGroup.Text>$</InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={partialAmount}
                    onChange={this.handleAmountChange}
                    autoFocus
                  />
                </InputGroup>
                <Form.Text className="text-muted">
                  Maximum refund: ${depositAmount.toFixed(2)}
                </Form.Text>
              </Form.Group>
            </div>
          )}

          {refundChoice === 'full' && (
            <div className="alert alert-info mt-2">
              The full deposit of ${depositAmount.toFixed(2)} will be refunded to the customer's original payment method.
            </div>
          )}

          {refundChoice === 'partial' && partialAmount && parseFloat(partialAmount) > 0 && (
            <div className="alert alert-info mt-2">
              ${parseFloat(partialAmount).toFixed(2)} will be refunded. The remaining ${(depositAmount - parseFloat(partialAmount)).toFixed(2)} will be kept.
            </div>
          )}

          {refundChoice === 'none' && (
            <div className="alert alert-secondary mt-2">
              No refund will be issued. The full deposit of ${depositAmount.toFixed(2)} will be kept.
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={this.handleCancel} disabled={processing}>
            Cancel
          </Button>
          <Button
            variant={refundChoice === 'none' ? 'danger' : refundChoice === 'full' ? 'success' : refundChoice === 'partial' ? 'warning' : 'primary'}
            onClick={this.handleConfirm}
            disabled={!refundChoice || processing}
          >
            {processing ? 'Processing...' :
             refundChoice === 'full' ? 'Refund & Cancel Request' :
             refundChoice === 'partial' ? 'Partial Refund & Cancel' :
             refundChoice === 'none' ? 'Keep Deposit & Cancel' :
             'Select an option above'}
          </Button>
        </Modal.Footer>

        <style jsx>{`
          .action-buttons .btn {
            height: auto;
          }

          .gap-2 {
            gap: 0.5rem;
          }

          .job-info {
            border-left: 4px solid #007bff;
          }
        `}</style>
      </Modal>
    );
  }
}

export default DepositRefundModal;
